import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { buildEmbeddedBookingPageURL } from "../../../config/portal";
import { useOrganization } from "../../../shared/store/organizationStore";

/**
 * Fully public appointment booking page — no enduser session required to view.
 * Maps to old portal's /appointment-booking-page route.
 *
 * Embeds the legacy portal's booking page in an iframe using overrideSubdomain, so CloudFront
 * routes the request to the legacy portal instead of resolving it back to this app.
 *
 * Query params:
 * - businessId: the organization's business ID
 * - appointmentBookingPageId: the AppointmentBookingPage ID
 */
export const PublicBookingPage = () => {
  const { t } = useTranslation("appointments");
  const [searchParams] = useSearchParams();
  const organization = useOrganization();

  const businessId = searchParams.get("businessId");
  const appointmentBookingPageId = searchParams.get("appointmentBookingPageId");

  const iframeUrl =
    organization && businessId && appointmentBookingPageId
      ? buildEmbeddedBookingPageURL({
          organization: { subdomain: organization.subdomain },
          businessId,
          appointmentBookingPageId,
        })
      : null;

  if (!organization) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading booking page...")}</p>
        </div>
      </div>
    );
  }

  if (!iframeUrl) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-destructive">
          <p className="text-sm font-medium mb-1">{t("Unable to load booking page")}</p>
          <p className="text-xs text-muted-foreground">{t("Missing required parameters")}</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={iframeUrl}
      className="w-full h-screen border-0"
      title={t("Book Appointment")}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
};
